/**
 * Money is stored as integer paisa (1 PKR = 100 paisa). Never floating point.
 * All arithmetic here is integer arithmetic on safe integers; parsing rejects
 * anything ambiguous rather than silently rounding (plan section 7, 12).
 */

/**
 * Integer paisa. Branded by convention via this alias and the toPaisa/fromPaisa
 * constructors; arithmetic functions accept plain numbers and return Paisa.
 */
export type Paisa = number

const MAX_SAFE_PAISA = Number.MAX_SAFE_INTEGER

export class MoneyError extends Error {
  constructor(
    message: string,
    readonly code: 'invalid_amount' | 'out_of_range',
  ) {
    super(message)
    this.name = 'MoneyError'
  }
}

function toPaisa(n: number): Paisa {
  if (!Number.isSafeInteger(n)) {
    throw new MoneyError(`Amount out of safe integer range: ${n}`, 'out_of_range')
  }
  return n as Paisa
}

/**
 * Parse a PKR amount into integer paisa. Accepts a decimal string or an integer
 * number of paisa is NOT accepted here (use fromPaisa for that). Input may carry
 * thousand separators (comma). At most 2 decimal places; more is rejected, never
 * silently rounded. Negative values are allowed (refunds/reversals) but must be
 * explicitly signed with a leading '-'.
 */
export function pkrToPaisa(input: string | number): Paisa {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) throw new MoneyError('Amount is not finite', 'invalid_amount')
    // A raw number is interpreted as PKR; reject anything needing sub-paisa precision.
    return pkrToPaisa(numberToDecimalString(input))
  }

  const cleaned = input.trim().replace(/,/g, '').replace(/^Rs\.?\s*/i, '')
  if (cleaned === '' || cleaned === '-') throw new MoneyError('Empty amount', 'invalid_amount')
  if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) {
    throw new MoneyError(`Invalid PKR amount: "${input}"`, 'invalid_amount')
  }

  const negative = cleaned.startsWith('-')
  const body = negative ? cleaned.slice(1) : cleaned
  const [whole, frac = ''] = body.split('.')
  const fracPadded = (frac + '00').slice(0, 2)

  const wholeNum = Number(whole)
  if (!Number.isSafeInteger(wholeNum)) throw new MoneyError('Amount too large', 'out_of_range')

  const paisa = wholeNum * 100 + Number(fracPadded)
  if (paisa > MAX_SAFE_PAISA) throw new MoneyError('Amount out of safe range', 'out_of_range')
  return toPaisa(negative ? -paisa : paisa)
}

function numberToDecimalString(n: number): string {
  // Avoid scientific notation and float artifacts for money-sized numbers.
  if (Number.isInteger(n)) return String(n)
  const fixed = n.toFixed(2)
  // Reject sub-paisa precision instead of rounding silently.
  if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) {
    throw new MoneyError(`Amount has sub-paisa precision: ${n}`, 'invalid_amount')
  }
  return fixed
}

/** Wrap an already-computed integer paisa value. */
export function fromPaisa(paisa: number): Paisa {
  return toPaisa(paisa)
}

export const ZERO = toPaisa(0)

export function addPaisa(...values: number[]): Paisa {
  let total = 0
  for (const v of values) {
    total += v
    if (!Number.isSafeInteger(total)) throw new MoneyError('Sum out of safe range', 'out_of_range')
  }
  return toPaisa(total)
}

export function subPaisa(a: number, b: number): Paisa {
  return addPaisa(a, -b)
}

export function sumPaisa(values: Iterable<number>): Paisa {
  return addPaisa(...Array.from(values))
}

/** Multiply integer paisa by an integer quantity (e.g. count). No fractional multipliers. */
export function mulPaisa(paisa: number, integerQty: number): Paisa {
  if (!Number.isSafeInteger(integerQty)) {
    throw new MoneyError('Quantity must be an integer', 'invalid_amount')
  }
  const product = paisa * integerQty
  if (!Number.isSafeInteger(product)) throw new MoneyError('Product out of safe range', 'out_of_range')
  return toPaisa(product)
}

/**
 * Gross profit = net sale proceeds - purchase cost - directly allocated handset costs.
 * Kept here so the rule lives in one tested place (plan section 12).
 */
export function grossProfit(saleProceeds: number, purchaseCost: number, directCosts: Iterable<number> = []): Paisa {
  return subPaisa(saleProceeds, addPaisa(purchaseCost, ...Array.from(directCosts)))
}

/**
 * South Asian lakh/crore digit grouping, implemented directly so output is
 * identical in Node and every browser ICU (Intl 'en-PK' grouping is not
 * reliable across environments). e.g. 100000 -> "1,00,000".
 */
function groupLakh(digits: string): string {
  if (digits.length <= 3) return digits
  const last3 = digits.slice(-3)
  let rest = digits.slice(0, -3)
  const groups: string[] = []
  while (rest.length > 2) {
    groups.unshift(rest.slice(-2))
    rest = rest.slice(0, -2)
  }
  if (rest.length > 0) groups.unshift(rest)
  return [...groups, last3].join(',')
}

/** Format integer paisa as a PKR string with lakh grouping, e.g. "Rs 1,00,000.00". */
export function formatPKR(paisa: number, opts: { symbol?: boolean } = {}): string {
  const { symbol = true } = opts
  if (!Number.isSafeInteger(paisa)) throw new MoneyError('Amount out of safe range', 'out_of_range')
  const negative = paisa < 0
  const abs = Math.abs(paisa)
  const whole = Math.floor(abs / 100)
  const frac = String(abs % 100).padStart(2, '0')
  const grouped = groupLakh(String(whole))
  return `${negative ? '-' : ''}${symbol ? 'Rs ' : ''}${grouped}.${frac}`
}
